import { PricingRangeWithDiscountProps } from '../types';
import { orderPricingRange } from './orderPricingRange';

/**
 * Merges two arrays of pricing ranges by applying firstPayroll prices as discounts
 *
 * @param prices - Array of standard prices, sorted by minimum number of employees
 * @param firstPayrollPrices - Array of firstPayroll prices to apply as discounts
 *
 * @returns A merged price array where:
 * - Standard prices keep their base values
 * - firstPayroll prices are applied as discounts (discountPrices) on matching ranges
 * - firstPayroll ranges without matches create new entries
 *
 * Merge logic:
 * 1. For each standard price, find an applicable firstPayroll price (from >= firstPayroll.from)
 * 2. If found, add firstPayroll prices as discount
 * 3. For remaining firstPayroll prices:
 *    - Find the closest lower standard price
 *    - Create a new range with this price and firstPayroll discount
 * 4. Return array sorted by minimum number of employees
 *
 * @example
 * ```ts
 * const prices = [
 *   { from: 0, base: 100, price: 10 },
 *   { from: 10, base: 200, price: 8 }
 * ];
 *
 * const firstPayrollPrices = [
 *   { from: 0, base: 80, price: 8 }
 * ];
 *
 * mergePrices(prices, firstPayrollPrices);
 * // [
 * //   {
 * //     from: 0,
 * //     base: 100,
 * //     price: 10,
 * //     discountPrices: { base: 80, price: 8 }
 * //   },
 * //   { from: 10, base: 200, price: 8 }
 * // ]
 * ```
 */
export function mergePrices(
  prices: PricingRangeWithDiscountProps[],
  firstPayrollPrices: PricingRangeWithDiscountProps[],
) {
  const pricesOrdered = orderPricingRange(prices);
  const pricesReverse = pricesOrdered.reverse();
  const firstPayrollPricesOrdered = orderPricingRange(firstPayrollPrices);
  const firstPayrollPricesReverse = firstPayrollPricesOrdered.reverse();
  /**
   * Merge prices
   */
  const mergedPrices = pricesOrdered.map((price) => {
    const matchingFirstPayroll = firstPayrollPricesReverse.find((firstPayroll) => {
      return price.from >= firstPayroll.from;
    });
    if (matchingFirstPayroll) {
      return {
        ...price,
        discountPrices: {
          base: matchingFirstPayroll.base,
          obFees: matchingFirstPayroll.obFees,
          price: matchingFirstPayroll.price,
        },
      };
    }
    return price;
  });

  /**
   * Add firstPayroll prices that don't have a corresponding price in prices
   */
  const remainingfirstPayrollPrices = firstPayrollPricesReverse.reduce(
    (acc: PricingRangeWithDiscountProps[], firstPayroll) => {
      const matching = pricesOrdered.find(
        (price) => price.from === firstPayroll.from,
      );
      if (!matching) {
        const underPrice = pricesReverse.find((price) => {
          return price.from < firstPayroll.from;
        });

        if (underPrice) {
          acc.push({
            ...underPrice,
            from: firstPayroll.from,
            discountPrices: {
              base: firstPayroll.base,
              obFees: firstPayroll.obFees,
              price: firstPayroll.price,
            },
          });
        }
      }
      return acc;
    },
    [],
  );
  mergedPrices.push(...remainingfirstPayrollPrices);

  return orderPricingRange(mergedPrices);
}
